import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import { useGenerateMenuHighlight, useSavePromo } from '../../../../queries/promoDesigner';
import { useGetMenus, useGetMenu } from '../../../../queries/signageMenus';
import { useGetWallMediaLibrary } from '../../../../queries/videoWall';
import { useCustomToast } from '../../../Contexts/ToastContext';

/**
 * Menu Highlight (Phase 2B) — pick a menu, pick up to 6 dishes, add a title +
 * style + aspect → AI composes a promo poster FEATURING those dishes (real photos
 * + names + exact prices, composited server-side). Pick one → saved to the
 * Promotions gallery + media library via the shared save flow.
 */
const STYLES = ['appetizing_photo', 'elegant_premium', 'bold_vibrant', 'fun_playful', 'minimal_clean', 'festive'];
const ASPECTS = [
  { key: '16:9', icon: 'pi pi-window-maximize', ratio: '16 / 9' },
  { key: '9:16', icon: 'pi pi-mobile', ratio: '9 / 16' },
  { key: '1:1', icon: 'pi pi-stop', ratio: '1 / 1' },
];
const MAX_DISHES = 6;
const dataUrl = (img) => `data:${img.mime};base64,${img.b64}`;

export default function MenuHighlightDialog({ visible, onHide, onSaved }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const gen = useGenerateMenuHighlight();
  const save = useSavePromo();

  const { data: menus = [] } = useGetMenus();
  const [menuId, setMenuId] = useState(null);
  const { data: menu } = useGetMenu(menuId, !!menuId && visible);

  const [selected, setSelected] = useState([]); // item ids, in pick order
  const [title, setTitle] = useState('');
  const [style, setStyle] = useState('appetizing_photo');
  const [aspect, setAspect] = useState('16:9');
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [zoom, setZoom] = useState(null); // data URL of the option being previewed full-size
  const [error, setError] = useState(null);

  const { data: mediaRaw } = useGetWallMediaLibrary(visible);
  const mediaMap = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    const m = {};
    (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; });
    return m;
  }, [mediaRaw]);
  const thumb = (id) => mediaMap[id]?.thumb && mediaMap[id]?.thumb !== 'loading' ? mediaMap[id].thumb : (mediaMap[id]?.path || null);

  const dishes = useMemo(() => (menu?.sections ?? []).flatMap((s) => s.items ?? []), [menu]);
  const ratio = ASPECTS.find((a) => a.key === aspect)?.ratio || '16 / 9';
  const busy = gen.isLoading || save.isLoading;

  const reset = () => { setSelected([]); setTitle(''); setOptions([]); setPicked(null); setError(null); };
  const close = () => { reset(); setMenuId(null); onHide(); };

  const toggle = (id) => {
    setOptions([]); setPicked(null);
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id)
      : prev.length >= MAX_DISHES ? prev : [...prev, id]);
  };

  const doGenerate = async () => {
    if (!menuId || selected.length === 0) return;
    setError(null); setOptions([]); setPicked(null);
    try {
      const res = await gen.mutateAsync({ menu_id: menuId, item_ids: selected, title: title.trim() || undefined, style, aspect, count: 3 });
      if (res?.status === 422 || !res?.options?.length) {
        setError(res?.msg || t('video_wall.menu_highlight.gen_failed'));
        return;
      }
      setOptions(res.options);
    } catch { setError(t('video_wall.menu_highlight.gen_failed')); }
  };

  const persist = async () => {
    if (!picked) return;
    try {
      const promo = await save.mutateAsync({ token: picked, name: title.trim() || 'Menu Highlight' });
      if (!promo?.media_id) { setError(t('video_wall.menu_highlight.save_failed')); return; }
      showToast('success', t('video_wall.menu_highlight.title'), t('video_wall.promo_ai.saved_toast'));
      onSaved?.(promo);
      close();
    } catch { setError(t('video_wall.menu_highlight.save_failed')); }
  };

  const menuOptions = menus.map((m) => ({ label: m.name, value: m.id }));

  return (
    <Dialog visible={visible} onHide={close} style={{ width: 'min(96vw, 760px)' }}
      header={<span><i className="pi pi-images me-2" style={{ color: 'var(--pp-primary)' }} />{t('video_wall.menu_highlight.title')}</span>}>
      {error && <Message severity="error" text={error} className="w-100 mb-3" />}

      <div className="d-flex flex-column gap-3">
        {/* menu picker */}
        <div>
          <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_highlight.pick_menu')}</label>
          <Dropdown className="w-100" value={menuId} options={menuOptions} disabled={busy}
            onChange={(e) => { setMenuId(e.value); setSelected([]); setOptions([]); }}
            placeholder={t('video_wall.menu_highlight.pick_menu_ph')} filter={menuOptions.length > 6} />
        </div>

        {/* dish multi-select */}
        {menuId && (
          <div>
            <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>
              {t('video_wall.menu_highlight.pick_dishes')} · {t('video_wall.menu_highlight.selected_n', { n: selected.length, max: MAX_DISHES })}
            </label>
            {dishes.length === 0 ? (
              <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_highlight.no_dishes')}</p>
            ) : (
              <div className="row g-2" style={{ maxHeight: 280, overflowY: 'auto' }}>
                {dishes.map((it) => {
                  const on = selected.includes(it.id);
                  const name = it.name_ar || it.name_en || '—';
                  const tu = it.photo_media_id ? thumb(it.photo_media_id) : null;
                  return (
                    <div key={it.id} className="col-6 col-md-4">
                      <button type="button" onClick={() => toggle(it.id)} disabled={busy || (!on && selected.length >= MAX_DISHES)}
                        className="d-flex align-items-center gap-2 w-100 text-start p-1"
                        style={{ background: on ? 'var(--pp-btn-honey-soft, rgba(217,138,43,.12))' : 'transparent',
                          border: `2px solid ${on ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`, borderRadius: 10, cursor: 'pointer' }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flex: '0 0 auto',
                          background: 'var(--pp-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {tu ? <img src={tu} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <i className="pi pi-image" style={{ color: 'var(--pp-text-secondary)' }} />}
                        </div>
                        <span className="flex-fill" style={{ minWidth: 0 }}>
                          <span className="d-block text-truncate" style={{ color: 'var(--pp-text-primary)' }} dir="auto">{name}</span>
                          {it.price && <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{it.price} {menu?.currency}</span>}
                        </span>
                        {on && <i className="pi pi-check-circle" style={{ color: 'var(--pp-btn-honey)' }} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="small mt-1 mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_highlight.photo_hint')}</p>
          </div>
        )}

        {/* title + style + aspect */}
        {menuId && (
          <>
            <div>
              <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_highlight.title_label')}</label>
              <InputText className="w-100" value={title} maxLength={80} disabled={busy} dir="auto"
                onChange={(e) => setTitle(e.target.value)} placeholder={t('video_wall.menu_highlight.title_ph')} />
            </div>
            <div>
              <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.style')}</label>
              <div className="d-flex flex-wrap gap-2">
                {STYLES.map((s) => (
                  <Button key={s} size="small" label={t(`video_wall.promo_ai.style_${s}`)}
                    outlined={style !== s} onClick={() => setStyle(s)} disabled={busy} />
                ))}
              </div>
            </div>
            <div>
              <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.aspect')}</label>
              <div className="d-flex gap-2">
                {ASPECTS.map((a) => (
                  <Button key={a.key} size="small" icon={a.icon} label={t(`video_wall.promo_ai.aspect_${a.key.replace(':', '_')}`)}
                    outlined={aspect !== a.key} onClick={() => setAspect(a.key)} disabled={busy} />
                ))}
              </div>
            </div>
          </>
        )}

        {/* results */}
        {gen.isLoading ? (
          <div className="text-center py-5">
            <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
            <h6 className="mt-3 mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_highlight.composing')}</h6>
            <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_highlight.composing_hint')}</p>
          </div>
        ) : options.length ? (
          <>
            <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.pick_hint')}</p>
            <div className="row g-2">
              {options.map((o) => (
                <div key={o.token} className="col-4" style={{ position: 'relative' }}>
                  <button type="button" className="p-0 w-100" style={{ background: 'transparent', cursor: 'pointer',
                    border: `3px solid ${picked === o.token ? 'var(--pp-btn-honey)' : 'transparent'}`, borderRadius: 12 }}
                    onClick={() => setPicked(o.token)}>
                    <img src={dataUrl(o)} alt="" className="w-100" style={{ aspectRatio: ratio, objectFit: 'cover', borderRadius: 9 }} />
                  </button>
                  <Button type="button" icon="pi pi-search-plus" rounded aria-label={t('video_wall.promo_ai.zoom')}
                    onClick={(e) => { e.stopPropagation(); setZoom(dataUrl(o)); }}
                    style={{ position: 'absolute', top: 8, insetInlineEnd: 8, width: 30, height: 30,
                      background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff' }} />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-between gap-2">
              <Button text icon="pi pi-refresh" label={t('video_wall.promo_ai.regenerate')} onClick={doGenerate} disabled={busy} />
              <Button icon="pi pi-check" label={t('video_wall.promo_ai.use_promo')} disabled={!picked || busy} loading={save.isLoading} onClick={persist} />
            </div>
          </>
        ) : menuId ? (
          <Button icon="pi pi-sparkles" label={t('video_wall.menu_highlight.compose')}
            disabled={selected.length === 0 || busy} onClick={doGenerate} />
        ) : null}
      </div>

      {/* Full-size preview (lightbox) of a generated option */}
      <Dialog visible={!!zoom} onHide={() => setZoom(null)} dismissableMask draggable={false}
        style={{ width: 'min(96vw, 1000px)' }} header={t('video_wall.promo_ai.preview')}>
        {zoom && <img src={zoom} alt="" style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }} />}
      </Dialog>
    </Dialog>
  );
}
